import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Collapsible from 'react-native-collapsible';
import { Text } from 'react-native-paper';

import {
  CallIcon,
  LinkIcon,
  LocationIcon,
  MailIcon,
  PersonIcon
} from '@/assets';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { ListItem } from '@/features/list/components/ListItem';
import { ListSection } from '@/features/list/components/ListSection';
import { PersonAvatar } from '@/features/media/components/avatar/PersonAvatar';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { ImageCarousel } from '@/features/media/components/image/ImageCarousel';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { ShowMoreButton } from '@/shared/components/ShowMoreButton';
import { StateView } from '@/shared/components/state-view/StateView';
import { hasItems } from '@/shared/utils/array';
import { isEmptyOrNull } from '@/shared/utils/string';
import {
  handleOpenEmailAddress,
  handleOpenGps,
  handleOpenPhoneNumber,
  handleOpenUrl
} from '@/shared/utils/url';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    header: {
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 32,
      alignItems: 'center',
      gap: 16,
      backgroundColor: theme.colors.surfaceContainer
    },
    name: { textAlign: 'center' },
    list: { width: '100%', backgroundColor: 'transparent' },
    content: { paddingVertical: 32, paddingHorizontal: 16, gap: 32 },
    title: { paddingBottom: 16 },
    person: {
      marginBottom: 8,
      gap: 8,
      flexDirection: 'row',
      alignItems: 'center'
    }
  });

type Props = AppNativeStackScreenProps<typeof AppRoute.OrganizationProfile>;

export const OrganizationProfileScreen: FC<Props> = ({ route }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'screens.organization-profile'
  });

  const iconProps = { fill: theme.colors.primary, width: 24, height: 24 };

  const { id: organizationId } = route.params;

  const {
    data: organization,
    isLoading,
    isError
  } = useGetOrganizationById(organizationId);

  const {
    name,
    address,
    emailAddress,
    locationDescription,
    phone,
    url,
    logoImage,
    description,
    detailImages,
    overviewImage,
    facts,
    persons
  } = organization ?? {};

  const [expanded, setExpanded] = useState(false);

  const images = useMemo(() => {
    let images: ClientMediaItem[] = [];
    if (hasItems(detailImages)) images = detailImages;
    else if (overviewImage) images = [overviewImage];

    return images;
  }, [detailImages, overviewImage]);

  const handleOpenAddress = useCallback(async () => {
    await handleOpenGps(address?.gpsLocation);
  }, [address]);

  const handleOpenEmail = useCallback(async () => {
    await handleOpenEmailAddress(emailAddress);
  }, [emailAddress]);

  const handleOpenPhone = useCallback(async () => {
    await handleOpenPhoneNumber(phone);
  }, [phone]);

  const handleOpenOrganizationUrl = useCallback(async () => {
    await handleOpenUrl(url);
  }, [url]);

  return (
    <StateView
      isLoading={isLoading}
      isError={isError}
      backgroundColor={theme.colors.surfaceContainerLowest}
      contentContainerStyle={{ paddingBottom: 32 }}
      stateLabels={t('state', { returnObjects: true })}
    >
      <View style={styles.header}>
        {logoImage && (
          <ProfileAvatar
            viewSize={140}
            iconFallback={PersonIcon}
            mediaItem={logoImage}
            mediaSize={MediaItemSize.MEDIUM}
          />
        )}
        <View>
          <Text
            variant="headlineMedium"
            numberOfLines={2}
            ellipsizeMode="tail"
            style={styles.name}
          >
            {name}
          </Text>
          {!isEmptyOrNull(locationDescription) && (
            <Text
              variant="bodyLarge"
              style={styles.name}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {locationDescription}
            </Text>
          )}
        </View>
        <ListSection style={styles.list}>
          {address && (
            <ListItem
              icon={() => <LocationIcon {...iconProps} />}
              title={
                <View>
                  {address.name && (
                    <Text variant="bodyLarge">{address.name}</Text>
                  )}
                  {address.street && (
                    <Text variant="bodyLarge">{address.street}</Text>
                  )}
                  {(address.zip || address.city) && (
                    <Text variant="bodyLarge">
                      {[address.zip, address.city].filter(Boolean).join(' ')}
                    </Text>
                  )}
                </View>
              }
              hideRightArrow
              onPress={() => {
                handleOpenAddress();
              }}
            />
          )}
          {emailAddress && (
            <ListItem
              icon={() => <MailIcon {...iconProps} />}
              title={emailAddress}
              hideRightArrow
              onPress={() => {
                handleOpenEmail();
              }}
            />
          )}
          {phone && (
            <ListItem
              icon={() => <CallIcon {...iconProps} />}
              title={phone}
              hideRightArrow
              onPress={() => {
                handleOpenPhone();
              }}
            />
          )}
          {url && (
            <ListItem
              icon={() => <LinkIcon {...iconProps} />}
              title={url}
              hideRightArrow
              onPress={() => {
                handleOpenOrganizationUrl();
              }}
            />
          )}
        </ListSection>
      </View>
      <View style={styles.content}>
        {hasItems(images) && <ImageCarousel mediaItems={images} />}
        <View>
          <Text variant="titleLarge" style={styles.title}>
            {t('about')}
          </Text>

          <Collapsible
            collapsed={!expanded}
            collapsedHeight={168}
            renderChildrenCollapsed
          >
            <Text variant="bodyLarge">{description}</Text>
          </Collapsible>
          {
            // Todo: if the text is short and is less then 168px the height should be lessen and the more button should not be shown
          }
          <View style={{ alignSelf: 'flex-start', paddingTop: 16 }}>
            <ShowMoreButton
              expanded={expanded}
              onChange={() => setExpanded(!expanded)}
            />
          </View>
        </View>
        {hasItems(facts) && (
          <View>
            <Text variant="titleLarge" style={styles.title}>
              {t('facts')}
            </Text>
            {facts.map((fact) => (
              <ListItem
                color={theme.colors.onBackground}
                key={fact.name}
                title={fact.name}
                badge={fact.value}
                hideRightArrow
              />
            ))}
          </View>
        )}

        {hasItems(persons) && (
          <View>
            <Text variant="titleLarge" style={styles.title}>
              {t('people')}
            </Text>
            {persons.map((person) => (
              <View style={styles.person} key={person.id}>
                <PersonAvatar person={person} size={48} canOpenGallery />
                <View style={{ flexDirection: 'column' }}>
                  <Text variant="titleMedium">{person.name}</Text>
                  <Text variant="bodyMedium">{person.position}</Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    </StateView>
  );
};
